import clsx from "clsx";
import { FC, useEffect, useId, useState, useSyncExternalStore } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import repeatGhost from "rujira.ui/assets/images/repeat-ghost.gif";
import repeatGhostStill from "rujira.ui/assets/images/repeat-ghost.png";
import { Button, useGlobalModalContext } from "rujira.ui";
import {
  PrivateToastGhost,
  PrivateToastSuccessGhost,
} from "../../common/components/PrivateToastGhost";
import { useRedactedV2 } from "../../redacted/v2";
import "../../common/components/PrivateOperationSteps.scss";
import "./NewSpendingAccount.scss";

/** The current time, every `everyMs` while `active` (a countdown). */
const useClock = (active: boolean, everyMs = 1_000): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(timer);
  }, [active, everyMs]);
  return now;
};

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener?.("change", onChange);
  return () => query.removeEventListener?.("change", onChange);
};
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia(REDUCED_MOTION).matches;

const operationRunning = (phase?: string): boolean =>
  phase === "preparing" || phase === "submitting" || phase === "pending";

/**
 * Whether a new Spending account can be requested now, and if not, why:
 * shared by the ↻ next to "Private Account" and the privacy panel.
 */
export const useNewSpendingAccount = () => {
  const { t } = useTranslation("header");
  const v2 = useRedactedV2();
  const { showModal } = useGlobalModalContext();
  const { regeneration, spendingGeneration } = v2.modelB;
  const availableAt = regeneration.availableAt ?? 0;
  const waiting = availableAt > Date.now();
  // Seconds tick every second; a daily limit only needs a coarse clock.
  const now = useClock(
    waiting,
    waiting && availableAt - Date.now() > 120_000 ? 30_000 : 1_000
  );
  const waitMs = availableAt - now;
  const busy =
    regeneration.running ||
    v2.status !== "ready" ||
    operationRunning(v2.operation?.phase);
  const disabled = busy || waitMs > 0;
  const reason = regeneration.running
    ? t("newSpendingAccountCreating")
    : busy
      ? t("newSpendingAccountBusy")
      : waitMs >= 3_600_000
        ? t("newSpendingAccountCooldownHours", {
            hours: Math.ceil(waitMs / 3_600_000),
          })
        : waitMs >= 60_000
          ? t("newSpendingAccountCooldownMinutes", {
              minutes: Math.ceil(waitMs / 60_000),
            })
          : waitMs > 0
            ? t("newSpendingAccountCooldown", {
                seconds: Math.ceil(waitMs / 1_000),
              })
            : undefined;
  return {
    /** Only an unlocked account in the single Spending account mode. */
    offered: spendingGeneration !== undefined,
    running: regeneration.running,
    disabled,
    /** Why it is not available right now. */
    reason,
    open: () => {
      if (disabled) return;
      showModal({
        className: "private-action-modal",
        backgroundClose: false,
        hideCloseButton: true,
        // Creating the account switches to it; the result shows here.
        keepOnAccountChange: true,
        children: <NewSpendingAccountDialog />,
      });
    },
  };
};

/**
 * Which Spending account is in use, as a neutral count ("Spending 2"). Never
 * the address: the account itself is not something to copy or share.
 */
export const SpendingLabel: FC = () => {
  const { t } = useTranslation("header");
  const generation = useRedactedV2().modelB.spendingGeneration;
  if (generation === undefined) return null;
  return (
    <span className="new-spending__label fs-12 color-grey ml-1">
      {t("spendingGeneration", { number: generation + 1 })}
    </span>
  );
};

/**
 * The repeat ghost next to "Private Account", as tall as the account pill:
 * every new action uses a new Spending account from then on. On hover it
 * winds up into the ghost and back; it keeps turning while the account is
 * created. Reduced motion shows the still repeat symbol.
 */
export const NewSpendingAccountButton: FC = () => {
  const { t } = useTranslation("header");
  const account = useNewSpendingAccount();
  const [hover, setHover] = useState(false);
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    prefersReducedMotion,
    () => false
  );
  if (!account.offered) return null;
  const active = hover && !account.disabled;
  const moving = !reducedMotion && (active || account.running);
  return (
    <button
      type="button"
      className={clsx(
        "new-spending__button transparent w-5 h-5 flex ai-c jc-c ml-1 br-3 bg-black transition",
        account.disabled ? "arrow" : "bg-hover-darkGrey pointer"
      )}
      aria-label={t("newSpendingAccount")}
      aria-haspopup="dialog"
      aria-disabled={account.disabled || undefined}
      data-tooltip-id="global-tip"
      data-tooltip-content={account.reason ?? t("newSpendingAccount")}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onClick={account.open}>
      <img
        src={moving ? repeatGhost : repeatGhostStill}
        alt=""
        aria-hidden="true"
        className={clsx(
          "new-spending__icon block",
          active || account.running ? "filter-white" : "filter-grey",
          account.disabled && !account.running && "opacity-10"
        )}
      />
    </button>
  );
};

type RegenerationPhase = NonNullable<
  ReturnType<typeof useRedactedV2>["modelB"]["regeneration"]["phase"]
>;

/** The creation's steps: preparing, the two sponsored transactions, confirming. */
const stepOf = (phase?: RegenerationPhase) =>
  phase === "registration-confirming" || phase === "subwallet-confirming"
    ? "confirming"
    : phase === "registration-relaying" ||
        phase === "registration-simulation" ||
        phase === "registration-approval" ||
        phase === "subwallet-relaying"
      ? "creating"
      : "preparing";

/** The two sponsored transactions take about half a minute. */
const EXPECTED_MS = 30_000;

/**
 * Loading toast while a new Spending account is created, in the style of the
 * private action toasts: the step that runs now and a bar that never stands
 * still.
 */
export const NewSpendingSteps: FC<{ startedAt: number }> = ({ startedAt }) => {
  const { t } = useTranslation("header");
  const step = stepOf(useRedactedV2().modelB.regeneration.phase);
  const now = useClock(true, 250);
  const elapsed = Math.max(0, now - startedAt);
  const share = 0.95 * (1 - Math.exp(-elapsed / (EXPECTED_MS / 2)));
  return (
    <div className="private-steps private-steps--compact" role="status">
      <span className="private-steps__head">
        <strong className="fs-14">
          {step === "confirming"
            ? t("newSpendingStepConfirming")
            : step === "creating"
              ? t("newSpendingStepCreating")
              : t("newSpendingStepPreparing")}
        </strong>
      </span>
      <span className="private-steps__bar" aria-hidden="true">
        <span
          className="private-steps__fill private-steps__fill--busy color-teal"
          style={{ width: `${(share * 100).toFixed(2)}%` }}
        />
      </span>
    </div>
  );
};

/** Confirmation in the style of the private action dialogs. */
export const NewSpendingAccountDialog: FC = () => {
  const { t } = useTranslation("header");
  const { hideModal } = useGlobalModalContext();
  const v2 = useRedactedV2();
  const titleId = useId();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const running = creating || v2.modelB.regeneration.running;
  const create = async () => {
    setCreating(true);
    setError(undefined);
    // A transaction runs: the toast at the top says so while the dialog
    // waits. It turns into the result on success; an error stays here.
    const progress = toast.loading(
      <NewSpendingSteps startedAt={Date.now()} />,
      { icon: <PrivateToastGhost /> }
    );
    try {
      await v2.modelB.regenerate();
      hideModal();
      toast.success(t("newSpendingAccountReady"), {
        id: progress,
        icon: <PrivateToastSuccessGhost />,
      });
    } catch (cause) {
      toast.dismiss(progress);
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : "Private Mode could not complete this request. Please try again."
      );
    } finally {
      setCreating(false);
    }
  };
  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="modal__header">
        <h2 id={titleId}>{t("newSpendingAccount")}</h2>
      </div>
      <div className="new-spending__body px-3 condensed">
        <p className="new-spending__why color-grey">
          {t("newSpendingAccountWhy")}
        </p>
        <ul className="new-spending__points">
          <li>{t("newSpendingAccountNewActions")}</li>
          <li>{t("newSpendingAccountPositions")}</li>
          <li>{t("newSpendingAccountFunds")}</li>
        </ul>
        <p className="color-grey mt-1">
          {t("newSpendingAccountDuration")} {t("newSpendingAccountDaily")}
        </p>
        {running && (
          <p role="status" className="mt-1">
            {t("newSpendingAccountCreating")}
          </p>
        )}
        {error && !running && (
          <p role="alert" className="color-orange mt-1">
            {error}
          </p>
        )}
      </div>
      <div className="modal__footer px-3 py-2 flex ai-c jc-sb gap-1">
        <Button
          type="button"
          className="button--grey button--outline"
          label={t("common:cancel")}
          disabled={running}
          onClick={hideModal}
        />
        <Button
          type="button"
          className={clsx("button", running && "button--waiting")}
          label={t("newSpendingAccountCreate")}
          autoFocus
          disabled={running}
          onClick={() => void create()}
        />
      </div>
    </div>
  );
};
